import logger from '@/utils/client-logger';
import { getEventAdapter } from './adapters/eventAdapters';
import { ProviderRegistry } from './providerRegistry';
import { Provider, ProviderType, TrackingEvent } from './types';
import { generateEventId } from './utils/eventIdGenerator';

export class ReportingCenter {
  private static instance: ReportingCenter;

  static getInstance(): ReportingCenter {
    if (!ReportingCenter.instance) {
      ReportingCenter.instance = new ReportingCenter();
      // 暴露日志控制方法到window对象
      if (typeof window !== 'undefined') {
        (window as any).enableReportCenterLog = ReportingCenter.instance.enableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).disableReportCenterLog = ReportingCenter.instance.disableLogging.bind(
          ReportingCenter.instance,
        );
        (window as any).getReportCenterLogStatus = ReportingCenter.instance.getLoggingStatus.bind(
          ReportingCenter.instance,
        );
        (window as any).listReportCenterProviders = ReportingCenter.instance.listProviders.bind(
          ReportingCenter.instance,
        );
      }
    }
    return ReportingCenter.instance;
  }

  private registry: ProviderRegistry;
  private initialized = false;
  private eventQueue: Array<{
    event: TrackingEvent;
    providers?: ProviderType[];
    adaptionData?: unknown;
    adapterType?: string;
  }> = [];

  private constructor() {
    this.registry = new ProviderRegistry();
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    const providers = this.registry.getEnabledProviders();
    const initPromises = providers.map((provider) => provider.init());

    await Promise.allSettled(initPromises);

    // Process any events that were queued during initialization
    this.initialized = true;
    this.processQueue();
  }

  registerProvider(provider: Provider): void {
    this.registry.registerProvider(provider);
  }

  /**
   * 追踪事件
   * @param event 事件数据 (可以是原始 TrackingEvent 或业务数据)
   * @param specificProviders 指定的 Provider 类型数组
   * @param adapterType 适配器类型，用于触发对应适配器 (如 'checkout.initiate')
   */
  track(
    event: TrackingEvent,
    specificProviders?: ProviderType[],
    adaptionData?: unknown,
    adapterType?: string,
  ): void {
    // Queue events if system isn't initialized yet
    if (!this.initialized) {
      this.eventQueue.push({ event, providers: specificProviders, adaptionData, adapterType });
      return;
    }

    let providers = this.registry.getEnabledProviders();

    // If specific providers are requested, filter to only those
    if (specificProviders && specificProviders.length > 0) {
      providers = providers.filter((p) => specificProviders.includes(p.type));
    }

    // 当 SENSORS 在目标 providers 中时，自动将 SNOWPLOW 一并纳入（1:1 镜像）
    // 覆盖全部 specificProviders: [ProviderType.SENSORS] 调用点，无需逐一修改业务代码
    const hasSensors = providers.some((p) => p.type === ProviderType.SENSORS);
    const hasSnowplow = providers.some((p) => p.type === ProviderType.SNOWPLOW);
    if (hasSensors && !hasSnowplow) {
      const snowplowProvider = this.registry.getProvider(ProviderType.SNOWPLOW);
      if (snowplowProvider?.isEnabled) {
        providers = [...providers, snowplowProvider];
      }
    }

    // 如果有 adapterType，必须使用适配器
    if (adapterType) {
      const adapter = getEventAdapter(adapterType);
      if (!adapter) {
        logger.error(`❌ No adapter found for adapter type: ${adapterType}`);
        return;
      }

      // 生成统一的事件 ID
      const eventId = generateEventId();
      logger.log(`🎯 Using event adapter for type: ${adapterType}`, {
        originalData: event,
        targetProviders: providers.map((p) => p.type),
        eventId,
      });

      // 为每个 Provider 使用适配器生成适配后的事件
      providers.forEach((provider) => {
        if (!provider.isInitialized()) {
          logger.warn(`⚠️ Provider ${provider.type} is not initialized`);
          return;
        }

        const adaptedEvent = adapter(adaptionData, provider.type, eventId);
        if (!adaptedEvent) {
          logger.error(
            `❌ Adapter failed to generate event for provider ${provider.type} with adapter type: ${adapterType}`,
          );
          return;
        }

        logger.log(`📤 Sending adapted event to ${provider.type}:`, adaptedEvent);
        if (Array.isArray(adaptedEvent)) {
          adaptedEvent.forEach((evt) => provider.track(evt));
        } else {
          provider.track(adaptedEvent);
        }
      });
    } else {
      // 没有 adapterType，使用传统方式处理
      this.trackWithoutAdapter(event as TrackingEvent, providers);
    }
  }

  /**
   * 启用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时启用所有Provider的日志
   */
  enableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('enableLog' in provider && typeof provider.enableLog === 'function') {
        (provider as any).enableLog();
        logger.log(`✅ Enabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 禁用指定Provider的日志
   * @param providerTypes Provider类型数组，为空数组或undefined时禁用所有Provider的日志
   */
  disableLogging(providerTypes?: string[]): void {
    const providers = this.getTargetProviders(providerTypes);

    providers.forEach((provider) => {
      if ('disableLog' in provider && typeof provider.disableLog === 'function') {
        (provider as any).disableLog();
        logger.log(`❌ Disabled logging for ${provider.type} provider`);
      } else {
        logger.warn(`⚠️ Provider ${provider.type} does not support logging control`);
      }
    });

    logger.log('📊 Current logging status:', this.getLoggingStatus());
  }

  /**
   * 获取当前所有Provider的日志状态
   */
  getLoggingStatus(): Record<string, boolean | 'not_supported'> {
    const providers = this.registry.getAllProviders();
    const status: Record<string, boolean | 'not_supported'> = {};

    providers.forEach((provider) => {
      if ('loggingEnabled' in provider) {
        status[provider.type] = (provider as any).loggingEnabled;
      } else {
        status[provider.type] = 'not_supported';
      }
    });

    return status;
  }

  /**
   * 列出所有已注册的Provider
   */
  listProviders(): string[] {
    return this.registry.getAllProviders().map((provider) => provider.type);
  }

  /**
   * 获取特定类型的Provider实例
   */
  getProvider(type: ProviderType): Provider | undefined {
    return this.registry.getProvider(type);
  }

  /**
   * 不使用适配器的传统追踪方式
   */
  private trackWithoutAdapter(event: TrackingEvent, providers: Provider[]): void {
    providers.forEach((provider) => {
      if (provider.isInitialized()) {
        provider.track(event);
      }
    });
  }

  private processQueue(): void {
    if (this.eventQueue.length === 0) return;

    this.eventQueue.forEach(({ event, providers, adapterType, adaptionData }) =>
      this.track(event, providers, adaptionData, adapterType),
    );
    this.eventQueue = [];
  }

  /**
   * 获取目标Provider列表
   */
  private getTargetProviders(providerTypes?: string[]): Provider[] {
    const allProviders = this.registry.getAllProviders();

    // 如果没有指定类型或传入空数组，返回所有Provider
    if (!providerTypes || providerTypes.length === 0) {
      return allProviders;
    }

    // 过滤指定类型的Provider
    return allProviders.filter((provider) => providerTypes.includes(provider.type));
  }
}
